
import React, { useState, useEffect, useCallback } from 'react';
import { 
  LayoutDashboard, Package, Truck, FileText, Settings, Users, 
  Menu, Bell, Search, LogOut, ChevronRight, Plus, AlertTriangle, 
  Camera, CheckSquare, Calendar, Download, ShoppingCart,
  BarChart3, Lock, User as UserIcon, Coins, Wallet, AlertCircle,
  TrendingUp, ArrowUpRight, ArrowDownRight, Briefcase, Leaf
} from 'lucide-react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';

import { CHECKLIST_TEMPLATE } from './constants';
import { apiClient, authService, reportingService, permissionService } from './src/services';
import type { DashboardOverview, RevenueData, ProvenanceDistribution, OverdueInvoice, ContractExpirationReport, PermissionMap } from './src/services';
import { ENDPOINTS } from './src/config/api';
import { Button, Input, Card, Badge, Modal, useToast } from './components/ui';
import { SignaturePad } from './components/SignaturePad';
import { StockModule } from './components/StockModule'; 
import { ClientsModule } from './components/ClientsModule'; 
import { SalesModule } from './components/SalesModule';
import { RentalModule } from './components/RentalModule'; 
import { LoaModule } from './components/LoaModule'; 
import { ReportingModule } from './components/ReportingModule'; 
import { AdminModule } from './components/AdminModule';
import { ComptabiliteModule } from './components/ComptabiliteModule'; 


// --- Types ---

type View = 'dashboard' | 'stock' | 'sales' | 'rentals' | 'loa' | 'clients' | 'reporting' | 'comptabilite' | 'admin';

interface UserSession {
  id: string;
  username: string;
  name: string;
  role: string;
  avatar: string;
}

// Helper to extract initials for avatar
const getInitials = (name: string): string => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// --- Helper Functions ---
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(amount);
};

// --- Helper Components ---

const StatusBadge = ({ status }: { status: string }) => {
  let color: 'green' | 'red' | 'yellow' | 'blue' | 'gray' = 'gray';
  let label = status;

  switch (status) {
    case 'AVAILABLE': color = 'green'; label = 'DISPONIBLE'; break;
    case 'VALIDATED': color = 'green'; label = 'VALIDÉ'; break;
    case 'PAID': color = 'green'; label = 'PAYÉ'; break;
    case 'RENTED': color = 'blue'; label = 'LOUÉ'; break;
    case 'LOA': color = 'blue'; label = 'LOA / LEASING'; break;
    case 'PARTIAL': color = 'blue'; label = 'PARTIEL'; break;
    case 'MAINTENANCE': color = 'red'; label = 'MAINTENANCE'; break;
    case 'OVERDUE': color = 'red'; label = 'EN RETARD'; break;
    case 'CANCELLED': color = 'red'; label = 'ANNULÉ'; break;
    case 'SOLD': color = 'gray'; label = 'VENDU'; break;
    case 'DRAFT': color = 'yellow'; label = 'BROUILLON'; break;
  }
  return <Badge color={color}>{label}</Badge>;
};

// --- Branding Component ---

const SogabLogo = ({ className = "", size = "md" }: { className?: string, size?: "sm" | "md" | "lg" }) => {
  const textSize = size === "lg" ? "text-4xl" : size === "md" ? "text-2xl" : "text-lg";
  const subTextSize = size === "lg" ? "text-sm" : size === "md" ? "text-xs" : "text-[10px]";
  const taglineSize = size === "lg" ? "text-xs" : "text-[10px]";

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex items-baseline leading-none">
        <span className={`${textSize} font-black text-green-700 tracking-tight`}>SOGAB</span>
        <span className={`${textSize} font-bold text-orange-500 ml-1`}>Afrique</span>
        {size === 'lg' && <span className="ml-2 text-base font-bold text-slate-800">SARL</span>}
      </div>
      {size === 'lg' && (
        <div className={`mt-1 font-medium text-slate-600 uppercase tracking-wider ${taglineSize}`}>
          La référence de la mécanisation durable
        </div>
      )}
      {size !== 'lg' && (
         <div className={`text-slate-400 font-bold uppercase tracking-widest ${subTextSize}`}>
           SARL
         </div>
      )}
    </div>
  );
};

// --- Login Component ---

const ForgotPasswordModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setSubmitting(false);
      setSent(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await authService.forgotPassword(email.trim());
      setSent(true);
    } catch (err: any) {
      toast(err?.message || 'Erreur lors de la demande', 'error');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Mot de passe oublié">
      {sent ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm">
            Si un compte est associé à cet email, un lien de réinitialisation vient d'être envoyé. Le lien est valable 60 minutes.
          </div>
          <div className="flex justify-end">
            <Button onClick={onClose}>Fermer</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-slate-600">
            Saisissez votre email. Vous recevrez un lien à usage unique pour choisir un nouveau mot de passe.
          </p>
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
            <Button type="submit" loading={submitting} disabled={submitting || !email.trim()}>Envoyer</Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

const ResetPasswordView = ({ token, onDone }: { token: string; onDone: () => void }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const ruleStatus = PASSWORD_RULES.map(r => ({ label: r.label, ok: r.test(newPassword) }));
  const allRulesOk = ruleStatus.every(r => r.ok);
  const matches = newPassword.length > 0 && newPassword === confirm;
  const canSubmit = allRulesOk && matches && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await authService.resetPassword(token, newPassword);
      setDone(true);
    } catch (err: any) {
      toast(err?.message || 'Lien invalide ou expiré', 'error');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-600 to-orange-500"></div>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-8">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <SogabLogo size="lg" className="items-center text-center" />
          </div>
        </div>
        <h2 className="text-center text-xl font-medium text-slate-600">Réinitialisation du mot de passe</h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl shadow-slate-200/50 sm:rounded-lg sm:px-10 border border-slate-100">
          {done ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm">
                Mot de passe modifié. Vous pouvez maintenant vous connecter.
              </div>
              <Button className="w-full bg-green-700 hover:bg-green-800" onClick={onDone}>Aller à la page de connexion</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Nouveau mot de passe"
                type="password"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
              <ul className="text-xs space-y-1 bg-slate-50 rounded-md p-3 border border-slate-200">
                {ruleStatus.map(r => (
                  <li key={r.label} className={r.ok ? 'text-green-600' : 'text-slate-500'}>
                    <span className="inline-block w-4">{r.ok ? '✓' : '○'}</span> {r.label}
                  </li>
                ))}
              </ul>
              <Input
                label="Confirmer"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                error={confirm.length > 0 && !matches ? 'Les mots de passe ne correspondent pas' : undefined}
              />
              <Button type="submit" className="w-full bg-green-700 hover:bg-green-800" disabled={!canSubmit} loading={submitting}>Changer mon mot de passe</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const LoginView = ({ onLogin }: { onLogin: (user: UserSession) => void }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [isForgotOpen, setIsForgotOpen] = useState(false);

  useEffect(() => {
    if (attempts >= 3) {
      setLocked(true);
      const timer = setTimeout(() => {
        setAttempts(0);
        setLocked(false);
        setError('');
      }, 30000); // 30s lockout
      return () => clearTimeout(timer);
    }
  }, [attempts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return;
    setLoading(true);
    setError('');

    try {
      const response = await authService.login({ email: username, password });
      const user = response.user;
      onLogin({
        id: user.id,
        username: user.email,
        name: user.full_name,
        role: user.role,
        avatar: getInitials(user.full_name),
      });
    } catch (err: any) {
      setAttempts(prev => prev + 1);
      const message = err?.message || 'Identifiants incorrects.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-600 to-orange-500"></div>
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-green-100 rounded-full opacity-50 mix-blend-multiply filter blur-3xl"></div>
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-100 rounded-full opacity-50 mix-blend-multiply filter blur-3xl"></div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="flex justify-center mb-8">
           <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center">
              <SogabLogo size="lg" className="items-center text-center" />
           </div>
        </div>
        <h2 className="mt-2 text-center text-xl font-medium text-slate-600">Connexion ERP</h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-white py-8 px-4 shadow-xl shadow-slate-200/50 sm:rounded-lg sm:px-10 border border-slate-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="email@sogab-afrique.com"
              disabled={locked}
            />

            <div>
              <Input 
                label="Mot de passe" 
                type="password" 
                required 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                disabled={locked}
              />
              <div className="text-right mt-1">
                 <button type="button" onClick={() => setIsForgotOpen(true)} className="text-xs font-medium text-green-600 hover:text-green-500">Mot de passe oublié ?</button>
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4 border border-red-100">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{locked ? 'Compte bloqué temporairement' : 'Erreur de connexion'}</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{locked ? 'Trop de tentatives. Veuillez patienter 30 secondes.' : error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Button type="submit" className="w-full flex justify-center bg-green-700 hover:bg-green-800" loading={loading} disabled={locked}>
                {locked ? 'Verrouillé' : 'Se connecter'}
              </Button>
            </div>
          </form>

        </div>
      </div>
      
      <div className="text-center mt-8 text-xs text-slate-400">
        &copy; {new Date().getFullYear()} SOGAB Afrique SARL. Tous droits réservés.
      </div>
      <ForgotPasswordModal isOpen={isForgotOpen} onClose={() => setIsForgotOpen(false)} />
    </div>
  );
};

// --- Views ---

const DashboardView = ({ navigate, sites = [], selectedSite = 'all', onSiteChange, userRole }: { navigate: (v: View) => void; sites?: { id: string; nom: string }[]; selectedSite?: string; onSiteChange?: (site: string) => void; userRole?: string }) => {
  const [period, setPeriod] = useState('mois');
  const site = selectedSite;
  const setSite = onSiteChange || (() => {});

  // API data state
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardOverview | null>(null);
  const [machineOccupancy, setMachineOccupancy] = useState<{ disponible: number; en_location: number; en_loa: number; en_transit: number; total: number; taux_occupation: number } | null>(null);
  const [revenueData, setRevenueData] = useState<{ name: string; revenue: number }[]>([]);
  const [clientOriginData, setClientOriginData] = useState<{ name: string; value: number }[]>([]);
  const [alerts, setAlerts] = useState<{ msg: string; time: string; type: string }[]>([]);
  const [partialError, setPartialError] = useState(false);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        const overviewParams: { periode?: string; site_id?: string } = { periode: period };
        if (site !== 'all') overviewParams.site_id = site;

        const isMag = userRole === 'MAGASINIER';

        // Magasinier: only fetch stock-related data; other roles: fetch everything
        const promises: Promise<any>[] = [
          isMag ? reportingService.getStockOnlyOverview() : reportingService.getDashboardOverview(overviewParams),
          isMag ? Promise.resolve([]) : reportingService.getMonthlyRevenue(new Date().getFullYear()),
          isMag ? Promise.resolve([]) : reportingService.getProvenanceDistribution(),
          isMag ? Promise.resolve([]) : reportingService.getOverdueInvoices(),
          isMag ? Promise.resolve([]) : reportingService.getExpiringContracts(7),
          apiClient.get('/dashboard/machine-occupancy').catch(() => ({ data: { data: null } })),
        ];

        const [overviewRes, monthlyRes, provenanceRes, overdueRes, expiringRes, occupancyRes] = await Promise.allSettled(promises);

        const overview = overviewRes.status === 'fulfilled' ? overviewRes.value : null;
        const monthlyRevenue = monthlyRes.status === 'fulfilled' ? monthlyRes.value : [];
        const provenanceData = provenanceRes.status === 'fulfilled' ? provenanceRes.value : [];
        const overdueInvoices = overdueRes.status === 'fulfilled' ? overdueRes.value : [];
        const expiringContracts = expiringRes.status === 'fulfilled' ? expiringRes.value : [];
        const occupancyData = occupancyRes.status === 'fulfilled' ? occupancyRes.value?.data?.data : null;

        // Only show error for unexpected failures (not for magasinier skipped calls)
        const checkedResults = isMag ? [overviewRes, expiringRes] : [overviewRes, monthlyRes, provenanceRes, overdueRes, expiringRes];
        const hasFailures = checkedResults.some(r => r.status === 'rejected');
        setPartialError(hasFailures);

        setDashboardData(overview);
        if (occupancyData) setMachineOccupancy(occupancyData);

        // Transform revenue data for chart
        const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
        setRevenueData(monthlyRevenue.map((r: RevenueData, idx: number) => ({
          name: monthNames[idx] || r.period,
          revenue: r.revenue,
        })));

        // Transform provenance data for pie chart
        setClientOriginData(provenanceData.map((p: ProvenanceDistribution) => ({
          name: p.provenance,
          value: p.percentage,
        })));

        // Build alerts from real data
        const newAlerts: { msg: string; time: string; type: string }[] = [];

        // Add overdue invoice alerts
        overdueInvoices.slice(0, 2).forEach((inv: OverdueInvoice) => {
          newAlerts.push({
            msg: `Facture impayée: ${inv.invoice_code} - ${inv.client_nom} (${formatCurrency(inv.solde_restant)})`,
            time: `${inv.jours_retard} jours de retard`,
            type: 'finance',
          });
        });

        // Add expiring contract alerts
        expiringContracts.slice(0, 2).forEach((c: ContractExpirationReport) => {
          newAlerts.push({
            msg: `Contrat ${c.type === 'LOCATION' ? 'Location' : 'LOA'} expire bientôt: ${c.client_nom}`,
            time: `${c.jours_restants} jours restants`,
            type: c.type === 'LOCATION' ? 'rental' : 'finance',
          });
        });

        // Add critical stock alert if any
        if (overview && overview.stock.critical_items > 0) {
          newAlerts.push({
            msg: `Stock critique: ${overview.stock.critical_items} article(s) sous le seuil`,
            time: 'Maintenant',
            type: 'stock',
          });
        }

        setAlerts(newAlerts);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [period, site]);

  // Calculate occupancy rate from real stock quantities (counts actual units, not product records)
  const occupancyRate = machineOccupancy?.taux_occupation ?? 0;

  // Role-based visibility
  const isMagasinier = userRole === 'MAGASINIER';
  const isCommercial = userRole === 'COMMERCIAL';

  // Build KPI cards from real data (filtered by role)
  const ALL_KPI_CARDS = dashboardData ? [
    {
      label: 'CA Mensuel',
      value: formatCurrency(dashboardData.sales.revenue_month),
      sub: `Cumul annuel: ${formatCurrency(dashboardData.sales.revenue_year)}`,
      icon: Coins,
      color: 'bg-emerald-100 text-emerald-800',
      trend: null,
      target: 'reporting' as View,
      roles: ['ADMIN', 'GERANT', 'COMMERCIAL', 'COMPTABLE'],
    },
    {
      label: 'Total Produits',
      value: String(dashboardData.stock.total_products),
      sub: `Valeur: ${formatCurrency(dashboardData.stock.total_value)}`,
      icon: Package,
      color: 'bg-indigo-100 text-indigo-800',
      trend: null,
      target: 'stock' as View,
      roles: ['MAGASINIER', 'TECHNICIEN'],
    },
    {
      label: 'Machines Disponibles',
      value: String(dashboardData.stock.machines_available),
      sub: `${dashboardData.stock.machines_rented} louées · ${dashboardData.stock.machines_loa} LOA`,
      icon: Truck,
      color: 'bg-green-100 text-green-800',
      trend: null,
      target: 'stock' as View,
      roles: ['MAGASINIER', 'TECHNICIEN'],
    },
    {
      label: 'Stock Critique',
      value: String(dashboardData.stock.critical_items),
      sub: `${dashboardData.stock.machines_maintenance} en maintenance`,
      icon: AlertTriangle,
      color: dashboardData.stock.critical_items > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800',
      trend: null,
      target: 'stock' as View,
      roles: null, // visible to all
    },
    {
      label: 'En Maintenance',
      value: String(dashboardData.stock.machines_maintenance),
      sub: dashboardData.stock.machines_maintenance > 0 ? 'Machines indisponibles' : 'Aucune machine',
      icon: Settings,
      color: dashboardData.stock.machines_maintenance > 0 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800',
      trend: null,
      target: 'stock' as View,
      roles: ['MAGASINIER', 'TECHNICIEN'],
    },
    {
      label: 'Impayés',
      value: formatCurrency(dashboardData.sales.invoices_unpaid_value),
      sub: `${dashboardData.sales.invoices_unpaid} factures en retard`,
      icon: AlertCircle,
      color: dashboardData.sales.invoices_unpaid > 0 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800',
      trend: null,
      target: 'sales' as View,
      roles: ['ADMIN', 'GERANT', 'COMMERCIAL', 'COMPTABLE'],
    },
    {
      label: 'Locations Actives',
      value: String(dashboardData.contracts.rentals_active),
      sub: `Taux occ. ${occupancyRate}%`,
      icon: Calendar,
      color: 'bg-blue-100 text-blue-800',
      trend: null,
      target: 'rentals' as View,
      roles: null, // visible to all
    },
    {
      label: 'Contrats LOA',
      value: String(dashboardData.contracts.loa_active),
      sub: dashboardData.contracts.loa_in_default > 0 ? `${dashboardData.contracts.loa_in_default} en défaut` : 'Tous à jour',
      icon: Briefcase,
      color: dashboardData.contracts.loa_in_default > 0 ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800',
      trend: null,
      target: 'loa' as View,
      roles: ['ADMIN', 'GERANT', 'COMMERCIAL', 'COMPTABLE'],
    },
  ] : [];
  const KPI_CARDS = ALL_KPI_CARDS.filter(k => !k.roles || !userRole || k.roles.includes(userRole));

  const COLORS = ['#16a34a', '#3b82f6', '#f59e0b', '#ef4444'];

  return (
    <div className="space-y-6">
      {/* Filters & Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Tableau de Bord</h2>
          <p className="text-sm text-slate-500">Aperçu global de l'activité - {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          {partialError && (
            <p className="text-xs text-amber-600 flex items-center gap-1 mt-1"><AlertTriangle className="w-3 h-3" /> Certaines données n'ont pas pu être chargées</p>
          )}
        </div>
        
        <div className="flex gap-3 bg-white p-1.5 rounded-lg border border-slate-200 shadow-sm">
          <select 
            className="text-sm border-none bg-transparent focus:ring-0 font-medium text-slate-700 cursor-pointer hover:bg-slate-50 rounded px-2"
            value={site} onChange={(e) => setSite(e.target.value)}
          >
            <option value="all">Tous les sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
          <div className="w-px bg-slate-200 my-1"></div>
          <select 
            className="text-sm border-none bg-transparent focus:ring-0 font-medium text-slate-700 cursor-pointer hover:bg-slate-50 rounded px-2"
            value={period} onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="mois">Ce mois</option>
            <option value="trimestre">Ce trimestre</option>
            <option value="annee">Cette année</option>
          </select>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {isLoading ? (
          // Loading skeleton
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm animate-pulse">
              <div className="flex justify-between items-start mb-2">
                <div className="p-2 rounded-lg bg-slate-200 w-9 h-9"></div>
              </div>
              <div>
                <div className="h-3 bg-slate-200 rounded w-20 mb-2"></div>
                <div className="h-6 bg-slate-200 rounded w-24 mb-2"></div>
                <div className="h-3 bg-slate-200 rounded w-28"></div>
              </div>
            </div>
          ))
        ) : (
          KPI_CARDS.map((k, i) => (
            <div
              key={i}
              onClick={() => navigate(k.target)}
              className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
            >
              <div className="flex justify-between items-start mb-2">
                <div className={`p-2 rounded-lg ${k.color}`}>
                  <k.icon className="w-5 h-5" />
                </div>
                {k.trend && (
                  <div className="flex items-center text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {k.trend}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{k.label}</p>
                <h3 className="text-xl font-bold text-slate-900 mt-1">{k.value}</h3>
                <p className="text-xs text-slate-500 mt-1 group-hover:text-primary-600 transition-colors flex items-center">
                  {k.sub} <ChevronRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Charts Grid - hidden for MAGASINIER/TECHNICIEN */}
      {!isMagasinier && userRole !== 'TECHNICIEN' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Evolution */}
        <Card title="Évolution du Chiffre d'Affaires" className="lg:col-span-2">
          <div className="h-72 w-full">
            {isLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : revenueData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-500">
                Aucune donnée de CA disponible
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} tickFormatter={(val) => `${val/1000000}M`} />
                  <Tooltip
                    cursor={{ fill: '#f8fafc' }}
                    formatter={(value: number) => [formatCurrency(value), "CA"]}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Bar dataKey="revenue" fill="#16a34a" radius={[4, 4, 0, 0]} barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Client Origin */}
        <Card title="Clients par Provenance">
          <div className="h-72 w-full flex flex-col items-center justify-center">
            {isLoading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            ) : clientOriginData.length === 0 ? (
              <div className="text-slate-500">Aucune donnée disponible</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={clientOriginData}
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    cy="50%"
                  >
                    {clientOriginData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => `${val}%`} />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
      )}

      {/* Secondary Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Occupancy Rate */}
        <Card title="Taux d'Occupation Machines">
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : dashboardData ? (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="relative w-40 h-40">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="12" />
                  <circle
                    cx="50" cy="50" r="40" fill="none"
                    stroke={occupancyRate >= 70 ? '#16a34a' : occupancyRate >= 50 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${occupancyRate * 2.51} 251`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-slate-800">{occupancyRate}%</span>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4 text-center text-sm">
                <div>
                  <p className="text-slate-500">Disponibles</p>
                  <p className="font-bold text-green-600">{machineOccupancy?.disponible ?? 0}</p>
                </div>
                <div>
                  <p className="text-slate-500">En location</p>
                  <p className="font-bold text-blue-600">{machineOccupancy?.en_location ?? 0}</p>
                </div>
                <div>
                  <p className="text-slate-500">En LOA</p>
                  <p className="font-bold text-purple-600">{machineOccupancy?.en_loa ?? 0}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500">
              Aucune donnée disponible
            </div>
          )}
        </Card>

        {/* Quick Alerts / Mini List */}
        <Card title="Alertes Récentes">
          <div className="space-y-4">
            {alerts.length === 0 ? (
              <div className="text-center py-4 text-slate-500 text-sm">
                Aucune alerte en cours
              </div>
            ) : (
              alerts.map((alert, i) => (
                <div key={i} className="flex items-start p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${alert.type === 'stock' ? 'bg-orange-500' : alert.type === 'rental' ? 'bg-red-500' : 'bg-blue-500'}`}></div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-slate-800">{alert.msg}</p>
                    <p className="text-xs text-slate-500">{alert.time}</p>
                  </div>
                </div>
              ))
            )}
            <Button variant="ghost" className="w-full text-xs" onClick={() => navigate('reporting')}>Voir toutes les alertes</Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

// --- Layout Shell ---

const SidebarItem = ({ 
  icon: Icon, label, active, onClick, badge 
}: { 
  icon: any, label: string, active: boolean, onClick: () => void, badge?: string 
}) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors mb-1 ${
      active 
        ? 'bg-green-50 text-green-700' 
        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
    }`}
  >
    <div className="flex items-center">
      <Icon className={`mr-3 h-5 w-5 ${active ? 'text-green-600' : 'text-slate-400'}`} />
      {label}
    </div>
    {badge && <span className="bg-red-100 text-red-600 py-0.5 px-2 rounded-full text-xs">{badge}</span>}
  </button>
);

// --- Change Password Modal ---

const PASSWORD_RULES: { test: (s: string) => boolean; label: string }[] = [
  { test: s => s.length >= 10, label: 'Au moins 10 caractères' },
  { test: s => /[A-Z]/.test(s), label: 'Au moins une majuscule' },
  { test: s => /[a-z]/.test(s), label: 'Au moins une minuscule' },
  { test: s => /\d/.test(s), label: 'Au moins un chiffre' },
  { test: s => /[^A-Za-z0-9]/.test(s), label: 'Au moins un caractère spécial' },
];

const ChangePasswordModal = ({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void; onSuccess: () => void }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      setSubmitting(false);
    }
  }, [isOpen]);

  const ruleStatus = PASSWORD_RULES.map(r => ({ label: r.label, ok: r.test(newPassword) }));
  const allRulesOk = ruleStatus.every(r => r.ok);
  const matches = newPassword.length > 0 && newPassword === confirm;
  const differentFromCurrent = newPassword.length > 0 && newPassword !== currentPassword;
  const canSubmit = currentPassword.length > 0 && allRulesOk && matches && differentFromCurrent && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await authService.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast('Mot de passe modifié. Veuillez vous reconnecter.', 'success');
      onSuccess();
    } catch (err: any) {
      toast(err?.message || 'Erreur lors du changement de mot de passe', 'error');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Changer mon mot de passe">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Mot de passe actuel"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
        />
        <Input
          label="Nouveau mot de passe"
          type="password"
          autoComplete="new-password"
          required
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
        />
        <ul className="text-xs space-y-1 bg-slate-50 rounded-md p-3 border border-slate-200">
          {ruleStatus.map(r => (
            <li key={r.label} className={r.ok ? 'text-green-600' : 'text-slate-500'}>
              <span className="inline-block w-4">{r.ok ? '✓' : '○'}</span> {r.label}
            </li>
          ))}
          <li className={differentFromCurrent ? 'text-green-600' : 'text-slate-500'}>
            <span className="inline-block w-4">{differentFromCurrent ? '✓' : '○'}</span> Différent du mot de passe actuel
          </li>
        </ul>
        <Input
          label="Confirmer le nouveau mot de passe"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          error={confirm.length > 0 && !matches ? 'Les mots de passe ne correspondent pas' : undefined}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button type="submit" disabled={!canSubmit} loading={submitting}>Changer</Button>
        </div>
      </form>
    </Modal>
  );
};

interface AppShellProps {
  user: UserSession;
  permissions: PermissionMap | null;
  onLogout: () => void;
}

const AppShell = ({ user, permissions, onLogout }: AppShellProps) => {
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [notifications, setNotifications] = useState<{ msg: string; time: string; type: string }[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedSite, setSelectedSite] = useState('all');
  const [sites, setSites] = useState<{ id: string; nom: string }[]>([]);

  // Fetch sites from API
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await apiClient.get(ENDPOINTS.SITES);
        const siteList = response.data?.data || response.data || [];
        setSites(siteList.filter((s: any) => s.is_active !== false));
      } catch (error) {
        console.error('Error loading sites:', error);
      }
    };
    fetchSites();
  }, []);

  // Fetch notifications based on user permissions (wait until permissions are loaded)
  const permsLoaded = permissionService.isLoaded(permissions);
  const canReadVentes = permissionService.hasRead(permissions, 'VENTES');
  const canReadLocations = permissionService.hasRead(permissions, 'LOCATIONS');
  const canReadStock = permissionService.hasRead(permissions, 'STOCK');
  const canReadReporting = permissionService.hasRead(permissions, 'REPORTING');
  const isMagasinier = user?.role === 'MAGASINIER';
  const isTechnicien = user?.role === 'TECHNICIEN';

  // Granular notification rules:
  // - Invoice alerts: VENTES or REPORTING read, but NOT magasinier/technicien (they have VENTES:R for delivery notes only)
  // - Contract alerts: LOCATIONS read, but NOT magasinier/technicien
  // - Stock alerts: STOCK read (all stock-related roles)
  const canSeeInvoiceAlerts = (canReadVentes || canReadReporting) && !isMagasinier && !isTechnicien;
  const canSeeContractAlerts = canReadLocations && !isMagasinier && !isTechnicien;

  const fetchNotifications = useCallback(async () => {
    if (!permsLoaded) return;
    const alerts: { msg: string; time: string; type: string }[] = [];
    try {
      const { rentalService } = await import('./src/services/rentalService');
      const promises = await Promise.allSettled([
        canSeeInvoiceAlerts ? reportingService.getOverdueInvoices() : Promise.resolve([]),
        canSeeContractAlerts ? reportingService.getExpiringContracts(7) : Promise.resolve([]),
        canReadReporting ? reportingService.getDashboardOverview() : (canReadStock ? reportingService.getStockOnlyOverview() : Promise.resolve(null)),
        canSeeContractAlerts ? rentalService.listPendingDepositRefunds() : Promise.resolve([]),
      ]);

      const overdueInvoices = promises[0].status === 'fulfilled' ? (promises[0].value as any[]) : [];
      const expiringContracts = promises[1].status === 'fulfilled' ? (promises[1].value as any[]) : [];
      const overview = promises[2].status === 'fulfilled' ? (promises[2].value as DashboardOverview) : null;
      const pendingRefunds = promises[3].status === 'fulfilled' ? (promises[3].value as any[]) : [];

      // Overdue invoice alerts
      if (canSeeInvoiceAlerts) {
        overdueInvoices.slice(0, 3).forEach((inv: any) => {
          alerts.push({
            msg: `Facture impayée: ${inv.invoice_code || inv.code} - ${inv.client_nom} (${new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(inv.solde_restant || inv.montant_restant || 0)})`,
            time: inv.jours_retard ? `${inv.jours_retard} jours de retard` : 'En retard',
            type: 'finance',
          });
        });
      }

      // Expiring contract alerts
      if (canSeeContractAlerts) {
        expiringContracts.slice(0, 3).forEach((c: any) => {
          alerts.push({
            msg: `Contrat ${c.type === 'LOCATION' ? 'Location' : 'LOA'} expire bientôt: ${c.client_nom}`,
            time: `${c.jours_restants} jours restants`,
            type: 'rental',
          });
        });
      }

      // Pending deposit refund alerts
      if (canSeeContractAlerts && pendingRefunds.length > 0) {
        alerts.push({
          msg: `${pendingRefunds.length} caution(s) en attente de remboursement`,
          time: 'Action requise',
          type: 'finance',
        });
      }

      // Per-site stock alerts (STOCK permission)
      if (canReadStock) {
        try {
          const { stockService: stkSvc } = await import('./src/services/stockService');
          const siteAlerts = await stkSvc.getStockAlertsBySite();
          if (siteAlerts.total_alerts > 0) {
            // Show one alert per site with critical items
            Object.entries(siteAlerts.alerts_by_site).forEach(([siteName, items]) => {
              const critiqueCount = (items as any[]).filter((i: any) => i.is_critique).length;
              const label = critiqueCount > 0
                ? `${critiqueCount} article(s) critique(s)`
                : `${items.length} article(s) sous le seuil`;
              alerts.push({
                msg: `Stock ${siteName}: ${label}`,
                time: 'Maintenant',
                type: 'stock',
              });
            });
          }
        } catch {
          // Fallback to global alert
          if (overview && overview.stock.critical_items > 0) {
            alerts.push({
              msg: `Stock critique: ${overview.stock.critical_items} article(s) sous le seuil`,
              time: 'Maintenant',
              type: 'stock',
            });
          }
        }
      }

      // Machines in maintenance (STOCK permission)
      if (canReadStock && overview && overview.stock.machines_maintenance > 0) {
        alerts.push({
          msg: `${overview.stock.machines_maintenance} machine(s) en maintenance`,
          time: 'Maintenant',
          type: 'stock',
        });
      }
    } catch {
      // Silent fail
    }
    setNotifications(alerts);
    setUnreadCount(alerts.length);
  }, [permsLoaded, canSeeInvoiceAlerts, canSeeContractAlerts, canReadStock, canReadReporting]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close mobile menu when view changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [currentView]);

  // If permissions haven't loaded yet, default to hiding modules (fail-closed)
  const hasModuleRead = (mod: string) => permissionService.hasRead(permissions, mod as any);
  const hasModuleWrite = (mod: string) => permissionService.hasWrite(permissions, mod as any);

  const renderView = () => {
    switch(currentView) {
      case 'dashboard': return <DashboardView navigate={setCurrentView} sites={sites} selectedSite={selectedSite} onSiteChange={setSelectedSite} userRole={user?.role} />;
      case 'stock': return <StockModule canWrite={hasModuleWrite('STOCK')} selectedSiteId={selectedSite} />;
      case 'clients': return <ClientsModule canWrite={hasModuleWrite('CLIENTS')} />;
      case 'sales': return <SalesModule canWrite={hasModuleWrite('VENTES')} userRole={user?.role} />;
      case 'rentals': return <RentalModule canWrite={hasModuleWrite('LOCATIONS')} userRole={user?.role} userName={user?.name} />;
      case 'loa': return <LoaModule canWrite={hasModuleWrite('LOCATIONS')} userRole={user?.role} />;
      case 'reporting': return <ReportingModule navigate={setCurrentView} userRole={user?.role} />;
      case 'comptabilite': return <ComptabiliteModule userRole={user?.role} />;
      case 'admin': return hasModuleRead('ADMIN') ? <AdminModule canWrite={hasModuleWrite('ADMIN')} userRole={user?.role} /> : (
        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
          <div className="p-4 bg-red-100 rounded-full mb-4"><Lock className="w-8 h-8 text-red-500" /></div>
          <p className="text-slate-600 font-medium">Accès non autorisé</p>
          <p className="text-sm mt-1">Ce module est réservé aux administrateurs.</p>
          <Button variant="ghost" onClick={() => setCurrentView('dashboard')} className="mt-4">Retour Accueil</Button>
        </div>
      );
      default: return (
        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
          <div className="p-4 bg-slate-100 rounded-full mb-4"><Settings className="w-8 h-8" /></div>
          <p>Module en construction</p>
          <Button variant="ghost" onClick={() => setCurrentView('dashboard')}>Retour Accueil</Button>
        </div>
      );
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center px-6 border-b border-slate-200 bg-white">
           <SogabLogo size="md" />
        </div>
        
        <div className="p-4 overflow-y-auto h-[calc(100vh-4rem)] flex flex-col justify-between">
          <nav>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 mt-2">Principal</div>
            <SidebarItem icon={LayoutDashboard} label="Tableau de Bord" active={currentView === 'dashboard'} onClick={() => setCurrentView('dashboard')} />
            {hasModuleRead('STOCK') && (
              <SidebarItem icon={Package} label="Stocks" active={currentView === 'stock'} onClick={() => setCurrentView('stock')} />
            )}
            {hasModuleRead('VENTES') && (
              <SidebarItem icon={ShoppingCart} label="Ventes" active={currentView === 'sales'} onClick={() => setCurrentView('sales')} />
            )}

            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 mt-6">Contrats</div>
            {hasModuleRead('LOCATIONS') && (
              <SidebarItem icon={Calendar} label="Locations" active={currentView === 'rentals'} onClick={() => setCurrentView('rentals')} />
            )}
            {hasModuleRead('LOCATIONS') && (
              <SidebarItem icon={FileText} label="LOA / Crédit-Bail" active={currentView === 'loa'} onClick={() => setCurrentView('loa')} />
            )}
            {hasModuleRead('CLIENTS') && (
              <SidebarItem icon={Users} label="Clients" active={currentView === 'clients'} onClick={() => setCurrentView('clients')} />
            )}

            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 mt-6">Gestion</div>
            {hasModuleRead('REPORTING') && (
              <SidebarItem icon={BarChart3} label="Reporting" active={currentView === 'reporting'} onClick={() => setCurrentView('reporting')} />
            )}
            {hasModuleRead('REPORTING') && ['ADMIN', 'COMPTABLE', 'GERANT'].includes(user?.role || '') && (
              <SidebarItem icon={Wallet} label="Comptabilité" active={currentView === 'comptabilite'} onClick={() => setCurrentView('comptabilite')} />
            )}
            {hasModuleRead('ADMIN') && (
              <SidebarItem icon={Settings} label="Administration" active={currentView === 'admin'} onClick={() => setCurrentView('admin')} />
            )}
          </nav>
          
          <div className="border-t border-slate-100 pt-4">
             <div className="flex items-center px-3 py-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                   {user.avatar}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-slate-700">{user.name}</p>
                  <p className="text-xs text-slate-500">{user.role}</p>
                </div>
             </div>
             <button onClick={() => setIsPasswordModalOpen(true)} className="w-full flex items-center px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-md mb-1">
               <Lock className="mr-3 h-4 w-4" /> Changer mon mot de passe
             </button>
             <button onClick={onLogout} aria-label="Déconnexion" className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md">
               <LogOut className="mr-3 h-4 w-4" /> Déconnexion
             </button>
          </div>
        </div>
      </aside>
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={() => { setIsPasswordModalOpen(false); onLogout(); }}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:ml-64 min-w-0 transition-all duration-200">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 z-30 sticky top-0">
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Ouvrir le menu" className="md:hidden text-slate-500">
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="hidden md:flex items-center flex-1 max-w-lg ml-4">
            <div className="relative w-full">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-300" aria-hidden="true" />
              </span>
              <input aria-label="Recherche globale" disabled title="Recherche globale disponible prochainement" className="block w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md leading-5 bg-slate-100 placeholder-slate-400 cursor-not-allowed sm:text-sm" placeholder="Recherche globale (bientôt disponible)" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <select
              className="hidden sm:block text-sm border-slate-200 rounded-md py-1.5 pl-3 pr-8 bg-white text-slate-700 font-medium cursor-pointer"
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
            >
              <option value="all">Tous les sites</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.nom}</option>
              ))}
            </select>
            <div className="relative">
              <button
                onClick={() => { setIsNotifOpen(!isNotifOpen); if (!isNotifOpen) setUnreadCount(0); }}
                className="relative p-1 rounded-full text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Bell className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
              {isNotifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsNotifOpen(false)} />
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50 max-h-96 flex flex-col">
                    <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
                      <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
                      {notifications.length > 0 && <span className="text-xs text-slate-400">{notifications.length} alerte(s)</span>}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Aucune notification</p>
                        <p className="text-xs text-slate-400 mt-1">Tout est en ordre</p>
                      </div>
                    ) : (
                      <div className="overflow-y-auto flex-1">
                        {notifications.map((n, i) => (
                          <div key={i} className="px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex items-start gap-3">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${n.type === 'stock' ? 'bg-orange-500' : n.type === 'rental' ? 'bg-red-500' : 'bg-blue-500'}`} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-slate-800 leading-snug">{n.msg}</p>
                              <p className="text-xs text-slate-400 mt-0.5">{n.time}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Scrollable Main Area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {renderView()}
          </div>
        </main>
      </div>
      
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-30 bg-black bg-opacity-25 md:hidden" onClick={() => setIsMobileMenuOpen(false)}></div>
      )}
    </div>
  );
};

const App = () => {
  const [user, setUser] = useState<UserSession | null>(null);
  const [permissions, setPermissions] = useState<PermissionMap | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [resetToken, setResetToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('reset_token');
  });
  const { toast } = useToast();

  const clearResetToken = () => {
    setResetToken(null);
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const loadPermissions = async () => {
    try {
      const perms = await permissionService.fetchPermissions();
      setPermissions(perms);
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Keep permissions as null — hasRead will default to false (fail-closed)
      setPermissions(null);
    }
  };

  // Listen for auth:logout events dispatched by apiClient on token refresh failure
  useEffect(() => {
    const handleAuthLogout = () => {
      permissionService.clearCache();
      setPermissions(null);
      setUser(null);
    };
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  // Listen for 403 Forbidden events to show user-friendly toast
  useEffect(() => {
    const handleForbidden = (e: Event) => {
      const message = (e as CustomEvent).detail || 'Accès non autorisé';
      toast(message, 'error');
    };
    window.addEventListener('api:forbidden', handleForbidden);
    return () => window.removeEventListener('api:forbidden', handleForbidden);
  }, [toast]);

  // Restore session from localStorage on mount and validate token
  useEffect(() => {
    const init = async () => {
      const storedUser = authService.getUser();
      if (storedUser && authService.isAuthenticated()) {
        try {
          // Validate the token is still accepted by the server
          await apiClient.get('/auth/me');
          setUser({
            id: storedUser.id,
            username: storedUser.email,
            name: storedUser.full_name,
            role: storedUser.role,
            avatar: getInitials(storedUser.full_name),
          });
          await loadPermissions();
        } catch {
          // Token is invalid/expired — clear session
          try { await authService.logout(); } catch { /* ignore */ }
          setUser(null);
        }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const handleLogin = async (u: UserSession) => {
    setUser(u);
    setIsLoading(true);
    await loadPermissions();
    setIsLoading(false);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    permissionService.clearCache();
    setPermissions(null);
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (resetToken && !user) {
    return <ResetPasswordView token={resetToken} onDone={clearResetToken} />;
  }

  if (!user) {
    return <LoginView onLogin={handleLogin} />;
  }

  return <AppShell user={user} permissions={permissions} onLogout={handleLogout} />;
};

export default App;
